// Client for the tenant self-service endpoints: branding and team.
//
// Separate from tenantPortal.ts because that file is deliberately read-only
// and says so. These are the only tenant calls that write, and keeping the
// two apart means "can a partner change this?" is answerable by which file
// the call lives in.

import { ApiError, errorMessageFrom } from './client';

export interface TenantBranding {
  productName: string | null;
  legalName: string | null;
  supportEmail: string | null;
  websiteUrl: string | null;
  primaryColor: string | null;
  primaryStrongColor: string | null;
  accentColor: string | null;
  /** Page background. Null leaves our default. */
  backgroundColor: string | null;
  /** Cards and panels sitting on the background. */
  surfaceColor: string | null;
  logoUrl: string | null;
  faviconUrl: string | null;
  /** Banner across the top of the customer dashboard. */
  heroImageUrl: string | null;
  /** The new app: the logo for the coloured bands (sign-in, account top). */
  logoInverseUrl: string | null;
  /** The new app: text and rules. Null keeps the app's own ink. */
  inkColor: string | null;
  /** The new app: headings and buttons. One of `TenantSettings.fonts`. */
  displayFont: string | null;
  /** The new app: body text. One of `TenantSettings.fonts`. */
  textFont: string | null;
  /**
   * Languages to offer this partner's customers. Empty means all of them.
   * Can only narrow our list, never extend it.
   */
  locales: string[] | null;
}

export interface TenantSettings {
  tenantId: string;
  name: string;
  branding: TenantBranding;
  /** Read-only here. Changing where a tenant lives is a Coepi decision. */
  hosts: string[];
  modules: Record<string, boolean>;
  /** Read-only. Removing the badge is a paid add-on, not a setting. */
  poweredBy: boolean;
  /** The faces displayFont and textFont accept. Absent from an older backend. */
  fonts?: string[];
}

export interface TenantMember {
  id: string;
  email: string | null;
  role: 'admin' | 'member';
  active: boolean;
  /** False for someone invited who has never signed in. */
  accepted: boolean;
}

export interface TenantInvite {
  id: string;
  email: string | null;
  role: 'admin' | 'member';
  expiresAt?: any;
}

export interface TenantTeam {
  members: TenantMember[];
  invites: TenantInvite[];
}

export interface InvitePreview {
  email: string;
  role: 'admin' | 'member';
  tenantName: string;
}

const apiUrl = (path: string) => `${process.env.REACT_APP_API_URL}${path}`;

const request = async (
  path: string,
  fallbackError: string,
  init: RequestInit = {},
  token?: string
): Promise<any> => {
  const headers: Record<string, string> = { ...((init.headers as any) || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (init.body) headers['Content-Type'] = 'application/json';

  const response = await fetch(apiUrl(path), { ...init, headers });
  let data: any = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }
  if (!response.ok) {
    throw new ApiError(errorMessageFrom(data, fallbackError), response.status, data);
  }
  return data;
};

export const fetchTenantSettings = (token: string): Promise<TenantSettings> =>
  request('/tenant/settings', 'Could not load your settings.', {}, token);

export const saveTenantBranding = (
  token: string,
  changes: Partial<TenantBranding>
): Promise<{ updated: string[] }> =>
  request(
    '/tenant/settings',
    'Could not save those changes.',
    { method: 'PATCH', body: JSON.stringify(changes) },
    token
  );

export const fetchTenantTeam = (token: string): Promise<TenantTeam> =>
  request('/tenant/team', 'Could not load your team.', {}, token);

export const inviteTeamMember = (
  token: string,
  email: string,
  role: 'admin' | 'member'
): Promise<{ token: string; email: string; role: string }> =>
  request(
    '/tenant/team/invites',
    'Could not create that invite.',
    { method: 'POST', body: JSON.stringify({ email, role }) },
    token
  );

export const deactivateTeamMember = (token: string, memberId: string): Promise<any> =>
  request(
    `/tenant/team/${encodeURIComponent(memberId)}`,
    'Could not remove that person.',
    { method: 'DELETE' },
    token
  );

export const setTeamMemberRole = (
  token: string,
  memberId: string,
  role: 'admin' | 'member'
): Promise<any> =>
  request(
    `/tenant/team/${encodeURIComponent(memberId)}/role`,
    'Could not change that role.',
    { method: 'POST', body: JSON.stringify({ role }) },
    token
  );

// The two public ones. No Bearer token on the preview: the recipient has no
// account with us yet, which is the whole point of an invite.
export const previewInvite = (inviteToken: string): Promise<InvitePreview> =>
  request(
    `/tenant/invites/${encodeURIComponent(inviteToken)}`,
    'This invite is no longer valid.'
  );

export const acceptInvite = (
  inviteToken: string,
  firebaseToken: string
): Promise<{ tenantId: string }> =>
  request(
    `/tenant/invites/${encodeURIComponent(inviteToken)}/accept`,
    'Could not accept this invite.',
    { method: 'POST' },
    firebaseToken
  );

/** The link an admin copies out of the portal and sends to a colleague. */
export const inviteLink = (inviteToken: string): string =>
  `${window.location.origin}/tenant/invite/${inviteToken}`;

// --- billing -----------------------------------------------------------
//
// The card itself never passes through here. These calls only mint and
// confirm a Stripe setup; the number goes from the browser straight to
// Stripe via their own iframe.

export interface TenantCard {
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
}

export interface TenantBilling {
  hasPaymentMethod: boolean;
  card: TenantCard | null;
  subscription: { status: string; currentPeriodEnd: number | null } | null;
  includedPerPeriod: number | null;
}

export const fetchTenantBilling = (token: string): Promise<TenantBilling> =>
  request('/tenant/billing', 'Could not load your billing details.', {}, token);

export const createCardSetup = (token: string): Promise<{ clientSecret: string }> =>
  request(
    '/tenant/billing/setup-intent',
    'Could not start adding a card.',
    { method: 'POST' },
    token
  );

/**
 * Tells the backend the card is confirmed so it becomes the default.
 *
 * Stripe attaching a payment method is not the same as it being used. Skip
 * this and the card sits on the account while every invoice still fails.
 */
export const confirmCardSetup = (
  token: string,
  setupIntentId: string
): Promise<{ ok: boolean }> =>
  request(
    '/tenant/billing/confirm',
    'Could not save that card.',
    { method: 'POST', body: JSON.stringify({ setup_intent_id: setupIntentId }) },
    token
  );

// Access codes: a partner who pays for their customers hands one to each customer instead of a
// price (lawyer-backend partners_app/tenant_portal/access_codes.py).

export type AccessCodeStatus = 'open' | 'claimed' | 'used' | 'revoked' | 'expired';

export interface AccessCode {
  id: string;
  label: string | null;
  status: AccessCodeStatus;
  createdAt: string | null;
  createdBy: string | null;
  expiresAt: string | null;
  usedAt: string | null;
  caseId: string | null;
}

export interface NewAccessCodes {
  /** The plain codes. Shown once: only their hashes are stored. */
  codes: { id: string; code: string }[];
  expiresAt: string;
  label: string | null;
}

export const fetchAccessCodes = (token: string): Promise<{ codes: AccessCode[] }> =>
  request('/tenant/access-codes', 'Could not load your access codes.', {}, token);

export const createAccessCodes = (
  token: string,
  body: { count: number; label?: string; validDays: number }
): Promise<NewAccessCodes> =>
  request(
    '/tenant/access-codes',
    'Could not make the codes.',
    { method: 'POST', body: JSON.stringify(body) },
    token
  );

export const revokeAccessCode = (token: string, id: string): Promise<{ revoked: boolean }> =>
  request(
    `/tenant/access-codes/${encodeURIComponent(id)}/revoke`,
    'Could not withdraw the code.',
    { method: 'POST' },
    token
  );
